<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationsStore } from '@/stores/notifications'
import { useSessionStore } from '@/stores/session'
import { timeAgo } from '@/lib/format'
import type { AppNotification } from '@/lib/types'

const router = useRouter()
const notifications = useNotificationsStore()
const session = useSessionStore()

const isOpen = ref(false)
const rootRef = ref<HTMLElement>()

function kindIcon(kind: string): string {
  if (kind.includes('action')) return 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z' // warning
  if (kind.includes('amend')) return 'M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a.996.996 0 000-1.41l-2.34-2.34a.996.996 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z' // edit
  if (kind.includes('deadline')) return 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z' // clock
  return 'M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z' // bell
}

function open(n: AppNotification) {
  isOpen.value = false
  if (!n.read_at) notifications.markRead(n.id)
  if (n.pursuit_id && session.activeOrgId) {
    router.push(`/o/${session.activeOrgId}/pursuits/${n.pursuit_id}`)
  }
}

function handleClickOutside(e: MouseEvent) {
  if (rootRef.value && !rootRef.value.contains(e.target as Node)) {
    isOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => document.removeEventListener('click', handleClickOutside))
</script>

<template>
  <div ref="rootRef" class="relative">
    <button
      class="relative p-2 rounded text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-short"
      aria-label="Notifications"
      @click="isOpen = !isOpen"
    >
      <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
      </svg>
      <span
        v-if="notifications.unreadCount"
        class="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-status-red text-white text-[10px] font-bold flex items-center justify-center"
      >
        {{ notifications.unreadCount > 99 ? '99+' : notifications.unreadCount }}
      </span>
    </button>

    <Transition
      enter-active-class="transition duration-medium ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-short ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="absolute right-0 top-full mt-2 w-[360px] rounded-lg border border-subtle bg-surface-card shadow-lg z-50 overflow-hidden"
      >
        <div class="flex items-center justify-between px-3 py-2 border-b border-subtle bg-surface-muted">
          <span class="text-label-caps uppercase text-ink-muted">Notifications</span>
          <button
            v-if="notifications.unreadCount"
            class="text-body-sm text-accent hover:text-accent-hover font-semibold"
            @click="notifications.markAllRead()"
          >
            Mark all read
          </button>
        </div>

        <div class="max-h-[380px] overflow-y-auto">
          <div v-if="!notifications.items.length" class="p-6 text-center text-body-sm text-ink-muted">
            Nothing here yet.
          </div>
          <button
            v-for="n in notifications.items"
            :key="n.id"
            class="w-full flex items-start gap-3 px-3 py-2.5 text-left border-b border-subtle last:border-b-0 transition-colors duration-short hover:bg-surface-muted"
            :class="n.read_at ? 'opacity-60' : ''"
            @click="open(n)"
          >
            <svg
              class="w-4 h-4 mt-0.5 flex-shrink-0"
              :class="n.kind.includes('action') ? 'text-status-amber' : 'text-ink-muted'"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path :d="kindIcon(n.kind)" />
            </svg>
            <div class="min-w-0 flex-1">
              <p class="text-body-sm text-ink line-clamp-2">{{ n.body }}</p>
              <p class="text-code-sm font-mono text-ink-muted mt-0.5">{{ timeAgo(n.created_at) }}</p>
            </div>
            <span v-if="!n.read_at" class="w-2 h-2 rounded-full bg-accent flex-shrink-0 mt-1.5" />
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>
